import React from 'react';
import { FormField, TextField, Nullable } from '@linetweet/linetweet-ui';
import clsx from 'clsx';

import styles from './PhoneNumberInput.module.scss';

interface PhoneNumberInputProps {
  value: Nullable<string>;
  onChange: (value: Nullable<string>) => void;
  label?: string;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  dataTestId?: string;
}

const normalizePhoneNumber = (input: string): string => {
  if (!input) return '';

  const normalized = input.normalize('NFKC');
  const withoutSpaces = normalized.replace(/\s/g, '');
  const firstDigitIndex = withoutSpaces.search(/\d/);
  const plusIndex = withoutSpaces.indexOf('+');
  const hasLeadingPlus = plusIndex !== -1 && (firstDigitIndex === -1 || plusIndex < firstDigitIndex);
  const digitsOnly = withoutSpaces.replace(/\D/g, '');
  return hasLeadingPlus ? '+' + digitsOnly : digitsOnly;
};

export function PhoneNumberInput(
  {
    name,
    label,
    value,
    onChange,
    placeholder,
    disabled,
    required,
    error,
    helperText,
    onBlur,
    dataTestId,
  }: PhoneNumberInputProps,
) {
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const normalized = normalizePhoneNumber(pastedText);
    onChange(normalized || null);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;
    const currentValue = value || '';
    const target = event.target as HTMLInputElement;
    const cursorPosition = target.selectionStart || 0;

    if (key === ' ') {
      event.preventDefault();
      return;
    }

    if (key === '+') {
      if (cursorPosition !== 0 || currentValue.includes('+')) {
        event.preventDefault();
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizePhoneNumber(event.target.value);
    onChange(normalized || null);
  };

  return (
    <FormField name={name} fullWidth>
      <TextField
        disabled={disabled}
        required={required}
        dismissKeyboardOnReturn
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        inputProps={{ 'data-testid': dataTestId }}
        value={value || ''}
        label={label}
        placeholder={placeholder}
        className={clsx(styles.input, { [styles.required]: required })}
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
      />
    </FormField>
  );
}

PhoneNumberInput.defaultProps = {
  label: undefined,
  name: undefined,
  placeholder: undefined,
  disabled: undefined,
  required: undefined,
  error: undefined,
  helperText: undefined,
  onBlur: undefined,
  dataTestId: undefined,
};
